import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { loadClarity, revokeClarity } from '../analytics/clarity'

const BASE_URL = process.env.REACT_APP_API_URL || ''

const CookieConsentContext = createContext(null)

const STORAGE_KEY = 'motorik_cookie_consent'
const VERSION = '2.0'

const isExpired = (iso) => {
  if (!iso) return true
  const saved = new Date(iso).getTime()
  if (Number.isNaN(saved)) return true
  const now = Date.now()
  const twelveMonthsMs = 365 * 24 * 60 * 60 * 1000
  return now - saved > twelveMonthsMs
}

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (isExpired(parsed.date)) return null
    if (!parsed.version || parsed.version === '1.0') return null
    return {
      necessary: true,
      analyticsInternal: !!parsed.analyticsInternal,
      analyticsClarity: !!parsed.analyticsClarity,
      date: parsed.date,
      version: parsed.version,
    }
  } catch {
    return null
  }
}

const writeConsent = (partial) => {
  const next = {
    necessary: true,
    analyticsInternal: !!partial.analyticsInternal,
    analyticsClarity: !!partial.analyticsClarity,
    date: new Date().toISOString(),
    version: VERSION,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  return next
}

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [withdrawalStatus, setWithdrawalStatus] = useState('idle')

  useEffect(() => {
    const current = readConsent()
    setConsent(current)
  }, [])

  useEffect(() => {
    if (consent && consent.analyticsClarity === true) {
      const id = process.env.REACT_APP_CLARITY_ID
      if (id && typeof id === 'string' && id.trim().length > 0) {
        loadClarity(id.trim())
      }
    }
  }, [consent])

  const doWithdrawalFetch = useCallback(() => {
    const csrfToken = process.env.REACT_APP_ORGANIZER_ANALYTICS_CSRF_TOKEN || 'motorik-withdrawal-csrf'
    setWithdrawalStatus('pending')
    fetch(`${BASE_URL}/api/v1/analytics/withdrawal`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Motorik-CSRF-Token': csrfToken },
    }).then((res) => {
      if (res.ok) {
        setWithdrawalStatus('success')
      } else {
        setWithdrawalStatus('error')
      }
    }).catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[analytics] withdrawal request failed — data may not be deleted server-side', err)
      }
      setWithdrawalStatus('error')
    })
  }, [])

  const dismissWithdrawal = useCallback(() => {
    setWithdrawalStatus('idle')
    setPanelOpen(false)
  }, [])

  const retryWithdrawal = useCallback(() => {
    if (withdrawalStatus === 'error') {
      doWithdrawalFetch()
    }
  }, [withdrawalStatus, doWithdrawalFetch])

  const updateConsent = useCallback((partial) => {
    const wasInternal = consent?.analyticsInternal === true
    const wasClarity = consent?.analyticsClarity === true
    const next = writeConsent(partial || {})

    if (next.analyticsInternal === true && !wasInternal) {
      const consentVersion = process.env.REACT_APP_ANALYTICS_CONSENT_VERSION
      fetch(`${BASE_URL}/api/v1/analytics/consent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consentVersion, decision: 'accepted' }),
      }).catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[analytics] consent POST failed — cookie may not be set server-side', err)
        }
      })
    }

    if (wasInternal && !next.analyticsInternal) {
      setConsent(next)
      if (wasClarity && !next.analyticsClarity) {
        revokeClarity()
      }
      doWithdrawalFetch()
      return
    }

    setConsent(next)
    setPanelOpen(false)

    if (wasClarity && !next.analyticsClarity) {
      revokeClarity()
    }
  }, [consent, doWithdrawalFetch])

  const openConfigPanel = useCallback(() => setPanelOpen(true), [])
  const closeConfigPanel = useCallback(() => setPanelOpen(false), [])

  const isBannerVisible = useMemo(() => consent === null, [consent])

  const value = useMemo(() => ({
    consent,
    updateConsent,
    openConfigPanel,
    closeConfigPanel,
    panelOpen,
    isBannerVisible,
    withdrawalStatus,
    dismissWithdrawal,
    retryWithdrawal,
  }), [consent, updateConsent, openConfigPanel, closeConfigPanel, panelOpen, isBannerVisible, withdrawalStatus, dismissWithdrawal, retryWithdrawal])

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export const useCookieConsent = () => useContext(CookieConsentContext)

export default CookieConsentContext

